/**
 * A tab opened before a deploy still asks for the previous build's hashed
 * chunks, and the Worker no longer serves them (404). The next client-side
 * navigation that needs a new chunk fails.
 *
 * The reload itself, with its one-a-minute guard, lives in the inline
 * `window.__mjStaleReload` script in `routes/__root.tsx` so `vite:preloadError`
 * can use it before any app code has loaded. This module is the React side.
 */

/** Errors browsers raise when a hashed chunk from the previous build is gone. */
export function isStaleChunkError(error: unknown): boolean {
  const msg = error instanceof Error ? `${error.name} ${error.message}` : String(error ?? "");
  return /dynamically imported module|Importing a module script failed|error loading dynamically imported|ChunkLoadError|Unable to preload CSS/i.test(
    msg,
  );
}

/**
 * Full page load into the new build. False when a reload already happened in
 * the last minute (the failure is real, so the error page should show) or on
 * the server.
 */
export function reloadForNewDeploy(): boolean {
  if (typeof window === "undefined") return false;
  const reload = (window as unknown as { __mjStaleReload?: () => boolean }).__mjStaleReload;
  return reload?.() ?? false;
}

/**
 * Inline <script> for `routes/__root.tsx`, kept here so it can be tested.
 * Allows one reload a minute (sessionStorage), so a genuinely fatal failure
 * cannot loop. `__mjReloading` covers the page that already asked: a route
 * often loses several chunks at once, and every failure after the first must
 * hear "reloading" too, or the guard says no and the 500 page flashes until
 * the reload lands.
 */
export const STALE_RELOAD_SCRIPT =
  "window.__mjStaleReload=function(){if(window.__mjReloading)return true;try{var t=+sessionStorage.getItem('mj_preload_reload')||0;if(Date.now()-t<60000)return false;sessionStorage.setItem('mj_preload_reload',String(Date.now()));}catch(_){}window.__mjReloading=true;var u=new URL(location.href);u.searchParams.set('_r',Date.now().toString());location.replace(u.toString());return true;};window.addEventListener('vite:preloadError',function(e){if(window.__mjStaleReload())e.preventDefault();});";
